// components/workspace-timeline/useWorkspaceTimelineInteractionController.ts
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import type { KernelHandle } from '#types/types';
import { MIN_TIMELINE_TRACK_SPAN_MS } from '#lib/workspace-timeline';
import { clamp } from './presentation';
import type {
  ResolvedWorkspaceTimelineTrack,
  TimelineFileDropPreview,
  TimelineLaneRow,
  TrackDragMode,
  TrackDragTimingPreview,
  TrackLaneDragPreview,
} from './types';

const MIN_TRACK_SPAN_MS = MIN_TIMELINE_TRACK_SPAN_MS;
const SPLIT_SNAP_TO_SECOND_THRESHOLD_MS = 120;
const TRACK_EDGE_SNAP_THRESHOLD_MS = 140;

interface TimelineTrackSnapshot {
  startMs: number;
  endMs: number;
  sourceInMs: number;
}

type TimelineTrackSnapshotMap = Record<string, TimelineTrackSnapshot>;

interface TrackDragState {
  layerId: string;
  trackName: string;
  allowNewLaneDrop: boolean;
  insertLaneId: string;
  initialLaneId: string;
  initialLaneOrder: number;
  latestLaneId: string;
  latestLaneOrder: number;
  hasLaneMutated: boolean;
  hasParamMutated: boolean;
  initialEffectRatio: number;
  latestEffectRatio: number;
  allowSourceTrim: boolean;
  mode: TrackDragMode;
  pointerStartX: number;
  laneWidthPx: number;
  durationMsAtStart: number;
  mediaDurationMs: number | null;
  minSegmentMs: number;
  initial: TimelineTrackSnapshot;
  latest: TimelineTrackSnapshot;
  initialSnapshotsByLayerId: TimelineTrackSnapshotMap;
  latestSnapshotsByLayerId: TimelineTrackSnapshotMap;
  edgeSnapAnchorsMs: number[];
  hasMutated: boolean;
}

interface TimelineDropTarget {
  insertBeforeLayerId: string | null;
  previewLayerId: string | null;
  insertAtEnd: boolean;
}

interface TrackOrderDragState {
  trackName: string;
  pointerStartY: number;
  rowStepPx: number;
  initialIndex: number;
  latestIndex: number;
  initialTrackLayerIds: string[];
  latestTrackLayerIds: string[];
  hasMutated: boolean;
}

interface PointerSelectionEventLike {
  button: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

interface UseWorkspaceTimelineInteractionControllerArgs {
  kernel: React.RefObject<KernelHandle | null>;
  projectId: string;
  workspaceId: string;
  showVersionView: boolean;
  activeTool: string | null;
  resolvedTracks: ResolvedWorkspaceTimelineTrack[];
  resolvedTracksRef: React.MutableRefObject<ResolvedWorkspaceTimelineTrack[]>;
  laneRowsRef: React.MutableRefObject<TimelineLaneRow[]>;
  displayTrackOrderLayerIdsRef: React.MutableRefObject<string[]>;
  maxLaneOrderRef: React.MutableRefObject<number>;
  rulerRangeStartMsRef: React.MutableRefObject<number>;
  rulerRangeEndMsRef: React.MutableRefObject<number>;
  rulerRangeMsRef: React.MutableRefObject<number>;
  clampedCursorMsRef: React.MutableRefObject<number>;
  rulerRef: React.RefObject<HTMLDivElement | null>;
  pausePlaybackForEdit: () => void;
  selectTrackFromPointer: (
    track: { layerId: string },
    event: PointerSelectionEventLike,
    forceSingleSelection?: boolean
  ) => void;
  setActiveDragTrackId: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveTrackDragInitialLaneId: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveTrackOrderDragId: React.Dispatch<React.SetStateAction<string | null>>;
  setDragSnapGuideMs: React.Dispatch<React.SetStateAction<number | null>>;
  setTrackDragTimingPreview: React.Dispatch<React.SetStateAction<TrackDragTimingPreview | null>>;
  setTrackLaneDragPreview: React.Dispatch<React.SetStateAction<TrackLaneDragPreview | null>>;
  setTimelineFileDropPreview: React.Dispatch<React.SetStateAction<TimelineFileDropPreview | null>>;
  setShowEndInsertLaneWhileDragging: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UseWorkspaceTimelineInteractionControllerResult {
  timelineTrackLaneRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  timelineInsertEndLaneRef: React.MutableRefObject<HTMLDivElement | null>;
  handleTimelineDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  handleTimelineDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  handleTimelineDrop: (event: React.DragEvent<HTMLDivElement>) => Promise<void>;
  registerTrackLaneRef: (laneTracks: ResolvedWorkspaceTimelineTrack[], element: HTMLDivElement | null) => void;
  startTrackDrag: (
    track: ResolvedWorkspaceTimelineTrack,
    mode: TrackDragMode,
    event: React.MouseEvent<HTMLElement>
  ) => void;
  startTrackOrderDrag: (track: ResolvedWorkspaceTimelineTrack, event: React.MouseEvent<HTMLElement>) => void;
  resolveSplitMsFromLanePointer: (
    track: ResolvedWorkspaceTimelineTrack,
    laneElement: HTMLElement,
    clientX: number
  ) => number | null;
  resolveTrackFromLanePointer: (
    laneTracks: ResolvedWorkspaceTimelineTrack[],
    laneElement: HTMLElement,
    clientX: number
  ) => ResolvedWorkspaceTimelineTrack | null;
}

const normalizeTrackSnapshot = (
  snapshot: TimelineTrackSnapshot,
  options?: { mediaDurationMs?: number | null; minSegmentMs?: number }
): TimelineTrackSnapshot => {
  const minSegmentMs = Math.max(1, options?.minSegmentMs ?? MIN_TRACK_SPAN_MS);
  const normalizedStartMs = Math.max(0, Math.round(snapshot.startMs));
  let normalizedSourceInMs = Math.max(0, Math.round(snapshot.sourceInMs));
  let normalizedDurationMs = Math.max(
    minSegmentMs,
    Math.round(snapshot.endMs) - normalizedStartMs
  );

  const mediaDurationMs =
    typeof options?.mediaDurationMs === 'number' && Number.isFinite(options.mediaDurationMs)
      ? Math.max(1, options.mediaDurationMs)
      : null;

  if (mediaDurationMs !== null) {
    const maxSourceInMs = Math.max(0, mediaDurationMs - minSegmentMs);
    normalizedSourceInMs = clamp(normalizedSourceInMs, 0, maxSourceInMs);
    const maxDurationMs = Math.max(minSegmentMs, mediaDurationMs - normalizedSourceInMs);
    normalizedDurationMs = clamp(normalizedDurationMs, minSegmentMs, maxDurationMs);
  }

  const normalizedEndMs = normalizedStartMs + normalizedDurationMs;
  return {
    startMs: normalizedStartMs,
    endMs: normalizedEndMs,
    sourceInMs: normalizedSourceInMs,
  };
};

const areTrackSnapshotsEqual = (
  left: TimelineTrackSnapshot,
  right: TimelineTrackSnapshot
): boolean =>
  left.startMs === right.startMs &&
  left.endMs === right.endMs &&
  left.sourceInMs === right.sourceInMs;

const areTrackSnapshotMapsEqual = (
  left: TimelineTrackSnapshotMap,
  right: TimelineTrackSnapshotMap
): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => {
    const leftSnapshot = left[key];
    const rightSnapshot = right[key];
    if (!leftSnapshot || !rightSnapshot) return false;
    return areTrackSnapshotsEqual(leftSnapshot, rightSnapshot);
  });
};

const areTrackDragTimingPreviewsEqual = (
  left: TrackDragTimingPreview | null,
  right: TrackDragTimingPreview | null
): boolean => {
  if (left === right) return true;
  if (!left || !right) return false;
  return left.layerId === right.layerId &&
    Object.is(left.fadeInRatio, right.fadeInRatio) &&
    Object.is(left.fadeOutRatio, right.fadeOutRatio) &&
    areTrackSnapshotMapsEqual(left.snapshotsByLayerId, right.snapshotsByLayerId);
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const buildTimelineFadeRatioPatch = (
  mode: TrackDragMode,
  ratio: number
): { fadeInRatio?: number; fadeOutRatio?: number } => (
  mode === 'fade-out-start' ? { fadeOutRatio: ratio } : { fadeInRatio: ratio }
);

const moveArrayItem = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex) return [...items];
  if (fromIndex < 0 || fromIndex >= items.length) return [...items];
  if (toIndex < 0 || toIndex >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  if (typeof item === 'undefined') return [...items];
  next.splice(toIndex, 0, item);
  return next;
};

export const useWorkspaceTimelineInteractionController = ({
  kernel,
  projectId,
  workspaceId,
  showVersionView,
  activeTool,
  resolvedTracks,
  resolvedTracksRef,
  laneRowsRef,
  displayTrackOrderLayerIdsRef,
  maxLaneOrderRef,
  rulerRangeStartMsRef,
  rulerRangeEndMsRef,
  rulerRangeMsRef,
  clampedCursorMsRef,
  rulerRef,
  pausePlaybackForEdit,
  selectTrackFromPointer,
  setActiveDragTrackId,
  setActiveTrackDragInitialLaneId,
  setActiveTrackOrderDragId,
  setDragSnapGuideMs,
  setTrackDragTimingPreview,
  setTrackLaneDragPreview,
  setTimelineFileDropPreview,
  setShowEndInsertLaneWhileDragging,
}: UseWorkspaceTimelineInteractionControllerArgs): UseWorkspaceTimelineInteractionControllerResult => {
  const trackDragStateRef = useRef<TrackDragState | null>(null);
  const trackDragListenersRef = useRef<{ move: (event: MouseEvent) => void; up: (event: MouseEvent) => void } | null>(null);
  const trackOrderDragStateRef = useRef<TrackOrderDragState | null>(null);
  const trackOrderDragListenersRef = useRef<{ move: (event: MouseEvent) => void; up: (event: MouseEvent) => void } | null>(null);
  const timelineTrackLaneRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const timelineInsertEndLaneRef = useRef<HTMLDivElement | null>(null);
  const endTrackDragRef = useRef<(commit: boolean) => void>(() => {});
  const endTrackOrderDragRef = useRef<(commit: boolean) => void>(() => {});
  const handleTrackDragMoveRef = useRef<(event: MouseEvent) => void>(() => {});
  const handleTrackOrderDragMoveRef = useRef<(event: MouseEvent) => void>(() => {});

  useEffect(() => {
    const activeLayerIds = new Set(resolvedTracks.map((track) => track.layerId));
    Object.keys(timelineTrackLaneRefs.current).forEach((layerId) => {
      if (!activeLayerIds.has(layerId)) {
        delete timelineTrackLaneRefs.current[layerId];
      }
    });
  }, [resolvedTracks]);

  const resolveSplitMsFromLanePointer = useCallback((
    track: ResolvedWorkspaceTimelineTrack,
    laneElement: HTMLElement,
    clientX: number
  ): number | null => {
    return kernel.current?.resolveWorkspaceTimelineSplitMsAtClientX({
      track,
      laneElement,
      clientX,
      timelineRenderRangeStartMs: rulerRangeStartMsRef.current,
      timelineRenderRangeMs: rulerRangeMsRef.current,
      snapToSecondThresholdMs: SPLIT_SNAP_TO_SECOND_THRESHOLD_MS,
    }) ?? null;
  }, [kernel, rulerRangeMsRef, rulerRangeStartMsRef]);

  const resolveTrackFromLanePointer = useCallback((
    laneTracks: ResolvedWorkspaceTimelineTrack[],
    laneElement: HTMLElement,
    clientX: number
  ): ResolvedWorkspaceTimelineTrack | null => {
    return kernel.current?.resolveWorkspaceTimelineTrackAtClientX({
      workspaceId,
      laneTracks,
      laneElement,
      clientX,
      timelineRenderRangeStartMs: rulerRangeStartMsRef.current,
      timelineRenderRangeMs: rulerRangeMsRef.current,
    }) ?? null;
  }, [kernel, rulerRangeMsRef, rulerRangeStartMsRef, workspaceId]);

  const resolveTimelineDropTargetFromClientY = useCallback((clientY: number): TimelineDropTarget => {
    const liveLaneRows = laneRowsRef.current;
    const rowBounds = liveLaneRows
      .map((laneRow) => {
        const laneElement = timelineTrackLaneRefs.current[laneRow.previewLayerId];
        if (!laneElement) return null;
        const rect = laneElement.getBoundingClientRect();
        if (!Number.isFinite(rect.top) || !Number.isFinite(rect.bottom)) return null;
        return {
          laneRow,
          top: rect.top,
          bottom: rect.bottom,
          center: (rect.top + rect.bottom) / 2,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
      .sort((left, right) => left.top - right.top);

    if (rowBounds.length === 0) {
      return { insertBeforeLayerId: null, previewLayerId: null, insertAtEnd: true };
    }

    const first = rowBounds[0];
    if (first && clientY < first.top) {
      return {
        insertBeforeLayerId: null,
        previewLayerId: null,
        insertAtEnd: true,
      };
    }

    const last = rowBounds[rowBounds.length - 1];
    if (last && clientY > last.bottom) {
      return {
        insertBeforeLayerId: last.laneRow.previewLayerId,
        previewLayerId: last.laneRow.previewLayerId,
        insertAtEnd: false,
      };
    }

    for (let index = 0; index < rowBounds.length; index += 1) {
      const row = rowBounds[index];
      if (!row) continue;
      if (clientY < row.top || clientY > row.bottom) continue;

      if (clientY <= row.center) {
        if (index === 0) {
          return {
            insertBeforeLayerId: null,
            previewLayerId: null,
            insertAtEnd: true,
          };
        }
        const previousRow = rowBounds[index - 1];
        if (!previousRow) {
          return {
            insertBeforeLayerId: null,
            previewLayerId: null,
            insertAtEnd: true,
          };
        }
        return {
          insertBeforeLayerId: previousRow.laneRow.previewLayerId,
          previewLayerId: previousRow.laneRow.previewLayerId,
          insertAtEnd: false,
        };
      }

      return {
        insertBeforeLayerId: row.laneRow.previewLayerId,
        previewLayerId: row.laneRow.previewLayerId,
        insertAtEnd: false,
      };
    }

    const nearest = rowBounds.reduce((closest, candidate) => {
      if (!closest) return candidate;
      const closestDistance = Math.abs(closest.center - clientY);
      const candidateDistance = Math.abs(candidate.center - clientY);
      return candidateDistance < closestDistance ? candidate : closest;
    }, null as (typeof rowBounds)[number] | null);

    if (!nearest) {
      return { insertBeforeLayerId: null, previewLayerId: null, insertAtEnd: true };
    }
    const nearestIndex = rowBounds.findIndex((row) => row.laneRow.previewLayerId === nearest.laneRow.previewLayerId);
    const insertAboveNearest = clientY <= nearest.center;
    if (insertAboveNearest) {
      if (nearestIndex <= 0) {
        return {
          insertBeforeLayerId: null,
          previewLayerId: null,
          insertAtEnd: true,
        };
      }
      const previousRow = rowBounds[nearestIndex - 1];
      if (!previousRow) {
        return {
          insertBeforeLayerId: null,
          previewLayerId: null,
          insertAtEnd: true,
        };
      }
      return {
        insertBeforeLayerId: previousRow.laneRow.previewLayerId,
        previewLayerId: previousRow.laneRow.previewLayerId,
        insertAtEnd: false,
      };
    }
    return {
      insertBeforeLayerId: nearest.laneRow.previewLayerId,
      previewLayerId: nearest.laneRow.previewLayerId,
      insertAtEnd: false,
    };
  }, [laneRowsRef]);

  const resolveTrackLanePreviewFromClientY = useCallback((
    clientY: number,
    options: {
      sourceLaneId: string;
      allowInsertAtEnd: boolean;
    }
  ): Omit<TrackLaneDragPreview, 'startMs' | 'durationMs' | 'hasConflict'> => {
    const liveLaneRows = laneRowsRef.current;
    const liveMaxLaneOrder = maxLaneOrderRef.current;
    const rowBounds = liveLaneRows
      .map((laneRow) => {
        const laneElement = timelineTrackLaneRefs.current[laneRow.previewLayerId];
        if (!laneElement) return null;
        const rect = laneElement.getBoundingClientRect();
        if (!Number.isFinite(rect.top) || !Number.isFinite(rect.bottom)) return null;
        return {
          laneRow,
          top: rect.top,
          bottom: rect.bottom,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
      .sort((left, right) => left.top - right.top);

    const sourceLaneOrder =
      liveLaneRows.find((laneRow) => laneRow.laneId === options.sourceLaneId)?.laneOrder ?? 0;
    const nextTopLaneOrder =
      Number.isFinite(liveMaxLaneOrder)
        ? Math.round(liveMaxLaneOrder) + 1
        : sourceLaneOrder + 1;

    if (rowBounds.length === 0) {
      if (options.allowInsertAtEnd) {
        return {
          mode: 'insert',
          targetLaneId: null,
          previewLayerId: null,
          insertBeforeLayerId: null,
          insertAtEnd: true,
          laneOrder: nextTopLaneOrder,
        };
      }
      return {
        mode: 'compact',
        targetLaneId: options.sourceLaneId,
        previewLayerId: null,
        insertBeforeLayerId: null,
        insertAtEnd: false,
        laneOrder: sourceLaneOrder,
      };
    }

    if (options.allowInsertAtEnd) {
      const insertEndLaneElement = timelineInsertEndLaneRef.current;
      if (insertEndLaneElement) {
        const insertEndRect = insertEndLaneElement.getBoundingClientRect();
        if (clientY >= insertEndRect.top && clientY <= insertEndRect.bottom) {
          return {
            mode: 'insert',
            targetLaneId: null,
            previewLayerId: null,
            insertBeforeLayerId: null,
            insertAtEnd: true,
            laneOrder: nextTopLaneOrder,
          };
        }
      }
    }

    const hoveredRow = rowBounds.find((row) => clientY >= row.top && clientY <= row.bottom);
    if (hoveredRow) {
      return {
        mode: 'compact',
        targetLaneId: hoveredRow.laneRow.laneId,
        previewLayerId: hoveredRow.laneRow.previewLayerId,
        insertBeforeLayerId: hoveredRow.laneRow.previewLayerId,
        insertAtEnd: false,
        laneOrder: hoveredRow.laneRow.laneOrder,
      };
    }

    const firstRow = rowBounds[0];
    if (options.allowInsertAtEnd && firstRow && clientY < firstRow.top) {
      return {
        mode: 'insert',
        targetLaneId: null,
        previewLayerId: null,
        insertBeforeLayerId: null,
        insertAtEnd: true,
        laneOrder: nextTopLaneOrder,
      };
    }

    const nearestRow = rowBounds.reduce((closest, candidate) => {
      if (!closest) return candidate;
      const closestDistance = Math.abs(((closest.top + closest.bottom) / 2) - clientY);
      const candidateDistance = Math.abs(((candidate.top + candidate.bottom) / 2) - clientY);
      return candidateDistance < closestDistance ? candidate : closest;
    }, null as (typeof rowBounds)[number] | null);

    if (!nearestRow) {
      return {
        mode: 'compact',
        targetLaneId: options.sourceLaneId,
        previewLayerId: null,
        insertBeforeLayerId: null,
        insertAtEnd: false,
        laneOrder: sourceLaneOrder,
      };
    }

    return {
      mode: 'compact',
      targetLaneId: nearestRow.laneRow.laneId,
      previewLayerId: nearestRow.laneRow.previewLayerId,
      insertBeforeLayerId: nearestRow.laneRow.previewLayerId,
      insertAtEnd: false,
      laneOrder: nearestRow.laneRow.laneOrder,
    };
  }, [laneRowsRef, maxLaneOrderRef]);

  const resolveTimelineMsFromClientX = useCallback((clientX: number): number => {
    const liveClampedCursorMs = clampedCursorMsRef.current;
    const liveRulerRangeStartMs = rulerRangeStartMsRef.current;
    const liveRulerRangeEndMs = rulerRangeEndMsRef.current;
    const liveRulerRangeMs = rulerRangeMsRef.current;
    const rulerElement = rulerRef.current;
    if (!rulerElement) {
      return Math.round(clamp(liveClampedCursorMs, liveRulerRangeStartMs, liveRulerRangeEndMs));
    }
    const rect = rulerElement.getBoundingClientRect();
    if (!Number.isFinite(rect.width) || rect.width <= 0) {
      return Math.round(clamp(liveClampedCursorMs, liveRulerRangeStartMs, liveRulerRangeEndMs));
    }
    const relativeX = clamp(clientX - rect.left, 0, rect.width);
    const relativeMs = (relativeX / rect.width) * liveRulerRangeMs;
    const timelineMs = clamp(liveRulerRangeStartMs + relativeMs, liveRulerRangeStartMs, liveRulerRangeEndMs);
    return Math.round(timelineMs);
  }, [clampedCursorMsRef, rulerRangeEndMsRef, rulerRangeMsRef, rulerRangeStartMsRef, rulerRef]);

  const inferTimelineDropLayerType = useCallback((dataTransfer: DataTransfer | null): TimelineFileDropPreview['layerType'] => {
    if (!dataTransfer) return 'unknown';
    const fileItems = Array.from(dataTransfer.items ?? []).filter((item) => item.kind === 'file');
    const inferFromMime = (mimeType: string): TimelineFileDropPreview['layerType'] => {
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('audio/')) return 'audio';
      return 'unknown';
    };

    for (const item of fileItems) {
      const layerType = inferFromMime(item.type || '');
      if (layerType !== 'unknown') return layerType;
    }
    const firstFile = dataTransfer.files?.[0];
    if (firstFile) return inferFromMime(firstFile.type || '');
    return 'unknown';
  }, []);

  const buildTimelineFileDropPreview = useCallback((event: React.DragEvent<HTMLDivElement>): TimelineFileDropPreview | null => {
    const hasFiles = Array.from(event.dataTransfer?.types ?? []).includes('Files');
    if (!hasFiles) return null;
    const startMs = resolveTimelineMsFromClientX(event.clientX);
    const target = resolveTimelineDropTargetFromClientY(event.clientY);
    return {
      startMs,
      insertBeforeLayerId: target.insertBeforeLayerId,
      previewLayerId: target.previewLayerId,
      insertAtEnd: target.insertAtEnd,
      layerType: inferTimelineDropLayerType(event.dataTransfer),
    };
  }, [inferTimelineDropLayerType, resolveTimelineDropTargetFromClientY, resolveTimelineMsFromClientX]);

  const clearTimelineFileDropPreview = useCallback(() => {
    setTimelineFileDropPreview(null);
  }, [setTimelineFileDropPreview]);

  const publishTrackDragTimingPreview = useCallback((preview: TrackDragTimingPreview | null) => {
    setTrackDragTimingPreview((current) => (
      areTrackDragTimingPreviewsEqual(current, preview) ? current : preview
    ));
  }, [setTrackDragTimingPreview]);

  const handleTimelineDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (showVersionView) return;
    const preview = buildTimelineFileDropPreview(event);
    if (!preview) return;
    event.preventDefault();
    event.stopPropagation();
    setTimelineFileDropPreview(preview);
  }, [buildTimelineFileDropPreview, setTimelineFileDropPreview, showVersionView]);

  const handleTimelineDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) {
      return;
    }
    clearTimelineFileDropPreview();
  }, [clearTimelineFileDropPreview]);

  const handleTimelineDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    if (showVersionView || !kernel.current) return;
    const preview = buildTimelineFileDropPreview(event);
    if (!preview) return;

    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    clearTimelineFileDropPreview();
    if (!files || files.length === 0) return;

    try {
      await kernel.current.addFilesAsLayers(files, 'timeline', undefined, {
        timelineDrop: {
          workspaceId,
          startMs: preview.startMs,
          insertBeforeLayerId: preview.insertBeforeLayerId,
        },
      });
    } catch (error: any) {
      kernel.current?.handleError(error?.message || 'Failed to add dropped media to timeline.');
    }
  }, [buildTimelineFileDropPreview, clearTimelineFileDropPreview, kernel, showVersionView, workspaceId]);

  const applyTrackSnapshotMapToLayers = useCallback((snapshotMap: TimelineTrackSnapshotMap) => {
    Object.entries(snapshotMap).forEach(([layerId, snapshot]) => {
      kernel.current?.updateTimelineTrackTiming(layerId, {
        startMs: snapshot.startMs,
        endMs: snapshot.endMs,
        sourceInMs: snapshot.sourceInMs,
      });
    });
  }, [kernel]);

  const applyTrackLayerOrderToWorkspace = useCallback((
    orderedTrackLayerIds: string[],
    options?: { undoable?: boolean; description?: string }
  ) => {
    if (orderedTrackLayerIds.length === 0) return;
    // UI drag order is top->bottom, while renderOrder/layerIds are persisted bottom->top.
    const trackOrder = [...orderedTrackLayerIds].reverse();
    const reorderedTrackIds = new Set(trackOrder);
    // The dragged order only covers the primary track of each lane, so the tracks it
    // does not name keep their current slot instead of being pushed to the end.
    const currentTrackOrder = kernel.current?.getWorkspaceTimelineProjection(projectId, workspaceId)?.trackLayerIds ?? [];
    let trackCursor = 0;
    const nextTrackOrder = currentTrackOrder.map((layerId) => {
      if (!reorderedTrackIds.has(layerId)) return layerId;
      const mappedLayerId = trackOrder[trackCursor];
      trackCursor += 1;
      return mappedLayerId ?? layerId;
    });
    kernel.current?.reorderWorkspaceTimelineTracks(projectId, workspaceId, nextTrackOrder, options);
  }, [kernel, projectId, workspaceId]);

  const clearTrackDragListeners = useCallback(() => {
    const listeners = trackDragListenersRef.current;
    if (!listeners) return;
    window.removeEventListener('mousemove', listeners.move);
    window.removeEventListener('mouseup', listeners.up);
    trackDragListenersRef.current = null;
  }, []);

  const clearTrackOrderDragListeners = useCallback(() => {
    const listeners = trackOrderDragListenersRef.current;
    if (!listeners) return;
    window.removeEventListener('mousemove', listeners.move);
    window.removeEventListener('mouseup', listeners.up);
    trackOrderDragListenersRef.current = null;
  }, []);

  const endTrackDrag = useCallback((commit: boolean) => {
    const dragState = trackDragStateRef.current;
    clearTrackDragListeners();
    trackDragStateRef.current = null;
    setActiveDragTrackId(null);
    setActiveTrackDragInitialLaneId(null);
    setDragSnapGuideMs(null);
    publishTrackDragTimingPreview(null);
    setTrackLaneDragPreview(null);
    setShowEndInsertLaneWhileDragging(false);
    if (!dragState || !kernel.current) {
      return;
    }

    if (!commit) {
      return;
    }

    if (!dragState.hasMutated) {
      return;
    }

    const beforeSnapshots = dragState.initialSnapshotsByLayerId;
    const afterSnapshots = dragState.latestSnapshotsByLayerId;
    const snapshotsChanged = !areTrackSnapshotMapsEqual(beforeSnapshots, afterSnapshots);
    const finalLaneId = dragState.latestLaneId;
    const finalLaneOrder = dragState.latestLaneOrder;
    const laneChanged = dragState.hasLaneMutated && (
      finalLaneId !== dragState.initialLaneId ||
      Math.abs(finalLaneOrder - dragState.initialLaneOrder) > 0.0001
    );

    const draggedSnapshot = afterSnapshots[dragState.layerId] ?? dragState.latest;
    const liveResolvedTracks = resolvedTracksRef.current;
    const laneConflict = liveResolvedTracks.some((track) => {
      if (track.layerId === dragState.layerId) return false;
      if (track.laneId !== finalLaneId) return false;
      return draggedSnapshot.startMs < track.endMs - 1 && draggedSnapshot.endMs > track.startMs + 1;
    });
    if (laneConflict) {
      toast.warning('Cannot place track in this rail: conflict with another track.');
      return;
    }

    const paramsChanged = dragState.hasParamMutated;
    if (!snapshotsChanged && !laneChanged && !paramsChanged) {
      return;
    }

    const description = `Adjust timeline track "${dragState.trackName}"`;
    kernel.current.executeAction({
      description,
      redo: () => {
        if (snapshotsChanged) {
          applyTrackSnapshotMapToLayers(afterSnapshots);
        }
        if (laneChanged) {
          kernel.current?.updateTimelineTrackLane(dragState.layerId, {
            laneId: finalLaneId,
            laneOrder: finalLaneOrder,
          });
          return;
        }
        if (paramsChanged) {
          kernel.current?.updateTimelineTrackAudio(
            dragState.layerId,
            buildTimelineFadeRatioPatch(dragState.mode, dragState.latestEffectRatio)
          );
        }
      },
      undo: () => {
        if (snapshotsChanged) {
          applyTrackSnapshotMapToLayers(beforeSnapshots);
        }
        if (laneChanged) {
          kernel.current?.updateTimelineTrackLane(dragState.layerId, {
            laneId: dragState.initialLaneId,
            laneOrder: dragState.initialLaneOrder,
          });
          return;
        }
        if (paramsChanged) {
          kernel.current?.updateTimelineTrackAudio(
            dragState.layerId,
            buildTimelineFadeRatioPatch(dragState.mode, dragState.initialEffectRatio)
          );
        }
      },
    });
  }, [
    applyTrackSnapshotMapToLayers,
    clearTrackDragListeners,
    kernel,
    publishTrackDragTimingPreview,
    resolvedTracksRef,
    setActiveDragTrackId,
    setActiveTrackDragInitialLaneId,
    setDragSnapGuideMs,
    setShowEndInsertLaneWhileDragging,
    setTrackLaneDragPreview,
  ]);

  const endTrackOrderDrag = useCallback((commit: boolean) => {
    const dragState = trackOrderDragStateRef.current;
    clearTrackOrderDragListeners();
    trackOrderDragStateRef.current = null;
    setActiveTrackOrderDragId(null);
    if (!dragState) return;

    const beforeOrder = dragState.initialTrackLayerIds;
    const afterOrder = dragState.latestTrackLayerIds;
    if (!commit) {
      if (dragState.hasMutated) {
        applyTrackLayerOrderToWorkspace(beforeOrder);
      }
      return;
    }
    if (!dragState.hasMutated || areStringArraysEqual(beforeOrder, afterOrder)) {
      return;
    }
    // The live drag already applied the new order, so rewind it before the undoable
    // commit: the Kernel snapshots the current state as the undo target.
    applyTrackLayerOrderToWorkspace(beforeOrder);
    applyTrackLayerOrderToWorkspace(afterOrder, {
      undoable: true,
      description: `Reorder timeline track "${dragState.trackName}"`,
    });
  }, [applyTrackLayerOrderToWorkspace, clearTrackOrderDragListeners, setActiveTrackOrderDragId]);

  const handleTrackDragMove = useCallback((event: MouseEvent) => {
    const dragState = trackDragStateRef.current;
    if (!dragState || !kernel.current) return;
    const liveResolvedTracks = resolvedTracksRef.current;
    const liveMaxLaneOrder = maxLaneOrderRef.current;
    event.preventDefault();

    const laneWidthPx = Math.max(1, dragState.laneWidthPx);
    const deltaPx = event.clientX - dragState.pointerStartX;
    const deltaMs = (deltaPx / laneWidthPx) * dragState.durationMsAtStart;
    const initial = dragState.initial;
    let nextSnapshot: TimelineTrackSnapshot;
    const nextSnapshotsByLayerId: TimelineTrackSnapshotMap = Object.fromEntries(
      Object.entries(dragState.initialSnapshotsByLayerId).map(([layerId, snapshot]) => [
        layerId,
        { ...snapshot },
      ])
    );

    const initialDurationMs = Math.max(dragState.minSegmentMs, initial.endMs - initial.startMs);
    const initialSourceOutMs = initial.sourceInMs + initialDurationMs;

    if (dragState.mode === 'move-track') {
      const rawNextStartMs = Math.max(0, initial.startMs + deltaMs);
      const rawNextEndMs = rawNextStartMs + initialDurationMs;

      const nearestAnchorForStart = dragState.edgeSnapAnchorsMs.reduce<{
        anchorMs: number;
        distanceMs: number;
      } | null>((closest, anchorMs) => {
        const distanceMs = Math.abs(anchorMs - rawNextStartMs);
        if (!closest || distanceMs < closest.distanceMs) {
          return { anchorMs, distanceMs };
        }
        return closest;
      }, null);

      const nearestAnchorForEnd = dragState.edgeSnapAnchorsMs.reduce<{
        anchorMs: number;
        distanceMs: number;
      } | null>((closest, anchorMs) => {
        const distanceMs = Math.abs(anchorMs - rawNextEndMs);
        if (!closest || distanceMs < closest.distanceMs) {
          return { anchorMs, distanceMs };
        }
        return closest;
      }, null);

      let nextStartMs = rawNextStartMs;
      let snappedGuideMs: number | null = null;

      const shouldSnapToTrackStart =
        nearestAnchorForStart !== null && nearestAnchorForStart.distanceMs <= TRACK_EDGE_SNAP_THRESHOLD_MS;
      const shouldSnapToTrackEnd =
        nearestAnchorForEnd !== null && nearestAnchorForEnd.distanceMs <= TRACK_EDGE_SNAP_THRESHOLD_MS;

      if (shouldSnapToTrackStart || shouldSnapToTrackEnd) {
        const snapStartDistanceMs = shouldSnapToTrackStart && nearestAnchorForStart
          ? nearestAnchorForStart.distanceMs
          : Number.POSITIVE_INFINITY;
        const snapEndDistanceMs = shouldSnapToTrackEnd && nearestAnchorForEnd
          ? nearestAnchorForEnd.distanceMs
          : Number.POSITIVE_INFINITY;
        if (snapStartDistanceMs <= snapEndDistanceMs) {
          nextStartMs = Math.max(0, nearestAnchorForStart?.anchorMs ?? rawNextStartMs);
          snappedGuideMs = nearestAnchorForStart?.anchorMs ?? null;
        } else {
          nextStartMs = Math.max(0, (nearestAnchorForEnd?.anchorMs ?? rawNextEndMs) - initialDurationMs);
          snappedGuideMs = nearestAnchorForEnd?.anchorMs ?? null;
        }
      }

      setDragSnapGuideMs(snappedGuideMs);
      nextSnapshot = {
        startMs: nextStartMs,
        endMs: nextStartMs + initialDurationMs,
        sourceInMs: initial.sourceInMs,
      };
    } else if (dragState.mode === 'trim-blue-start') {
      setDragSnapGuideMs(null);
      if (!dragState.allowSourceTrim) {
        const proposedStartMs = initial.startMs + deltaMs;
        const overflowBeforeZeroMs = proposedStartMs < 0 ? Math.round(-proposedStartMs) : 0;
        if (overflowBeforeZeroMs > 0) {
          Object.keys(nextSnapshotsByLayerId).forEach((layerId) => {
            const snapshot = nextSnapshotsByLayerId[layerId];
            if (!snapshot) return;
            nextSnapshotsByLayerId[layerId] = {
              startMs: snapshot.startMs + overflowBeforeZeroMs,
              endMs: snapshot.endMs + overflowBeforeZeroMs,
              sourceInMs: snapshot.sourceInMs,
            };
          });
        }
        const maxStartMs = Math.max(0, initial.endMs + overflowBeforeZeroMs - dragState.minSegmentMs);
        const nextStartMs = clamp(Math.max(0, proposedStartMs), 0, maxStartMs);
        nextSnapshot = {
          startMs: nextStartMs,
          endMs: initial.endMs + overflowBeforeZeroMs,
          sourceInMs: 0,
        };
      } else {
        const mediaDurationMs = dragState.mediaDurationMs;
        const minDeltaMs = Math.max(-initial.sourceInMs, -initial.startMs);
        const maxDeltaByOutMs = initialDurationMs - dragState.minSegmentMs;
        const maxDeltaByMediaMs =
          mediaDurationMs !== null
            ? Math.max(0, mediaDurationMs - dragState.minSegmentMs - initial.sourceInMs)
            : Number.MAX_SAFE_INTEGER;
        const maxDeltaMs = Math.min(maxDeltaByOutMs, maxDeltaByMediaMs);
        const clampedDeltaMs = clamp(deltaMs, minDeltaMs, Math.max(minDeltaMs, maxDeltaMs));
        const unclampedNextStartMs = initial.startMs + clampedDeltaMs;
        const nextStartMs = clamp(
          unclampedNextStartMs,
          0,
          Math.max(0, initial.endMs - dragState.minSegmentMs)
        );
        const nextSourceInMs = initial.sourceInMs + (nextStartMs - initial.startMs);
        nextSnapshot = {
          startMs: nextStartMs,
          endMs: initial.endMs,
          sourceInMs: nextSourceInMs,
        };
      }
    } else if (dragState.mode === 'fade-in-end' || dragState.mode === 'fade-out-start') {
      setDragSnapGuideMs(null);
      const boundaryInitialMs = dragState.mode === 'fade-in-end'
        ? (initial.startMs + initialDurationMs * dragState.initialEffectRatio)
        : (initial.endMs - initialDurationMs * dragState.initialEffectRatio);
      const nextBoundaryMs = clamp(boundaryInitialMs + deltaMs, initial.startMs, initial.endMs);
      const nextEffectRatio = dragState.mode === 'fade-in-end'
        ? clamp((nextBoundaryMs - initial.startMs) / Math.max(1, initialDurationMs), 0, 1)
        : clamp((initial.endMs - nextBoundaryMs) / Math.max(1, initialDurationMs), 0, 1);
      if (Math.abs(nextEffectRatio - dragState.latestEffectRatio) < 0.0005) {
        return;
      }

      dragState.latestEffectRatio = nextEffectRatio;
      dragState.hasParamMutated = Math.abs(nextEffectRatio - dragState.initialEffectRatio) > 0.0005;
      dragState.hasMutated = dragState.hasParamMutated;

      publishTrackDragTimingPreview({
        layerId: dragState.layerId,
        snapshotsByLayerId: {},
        fadeInRatio: dragState.mode === 'fade-in-end' ? nextEffectRatio : null,
        fadeOutRatio: dragState.mode === 'fade-out-start' ? nextEffectRatio : null,
      });
      return;
    } else {
      setDragSnapGuideMs(null);
      const mediaDurationMs = dragState.mediaDurationMs;
      const minSourceOutMs = initial.sourceInMs + dragState.minSegmentMs;
      const maxSourceOutMs =
        dragState.allowSourceTrim
          ? (mediaDurationMs !== null ? mediaDurationMs : initialSourceOutMs)
          : Number.MAX_SAFE_INTEGER;
      const nextSourceOutMs = clamp(
        initialSourceOutMs + deltaMs,
        minSourceOutMs,
        Math.max(minSourceOutMs, maxSourceOutMs)
      );
      const nextDurationMs = Math.max(dragState.minSegmentMs, nextSourceOutMs - initial.sourceInMs);
      nextSnapshot = {
        startMs: initial.startMs,
        endMs: initial.startMs + nextDurationMs,
        sourceInMs: initial.sourceInMs,
      };
    }

    let normalizedNextSnapshot = normalizeTrackSnapshot(nextSnapshot, {
      mediaDurationMs: dragState.mediaDurationMs,
      minSegmentMs: dragState.minSegmentMs,
    });
    if (dragState.mode === 'trim-blue-start') {
      const clampedStartMs = clamp(
        normalizedNextSnapshot.startMs,
        0,
        Math.max(0, normalizedNextSnapshot.endMs - dragState.minSegmentMs)
      );
      if (dragState.allowSourceTrim) {
        const sourceDeltaMs = clampedStartMs - initial.startMs;
        normalizedNextSnapshot = normalizeTrackSnapshot({
          startMs: clampedStartMs,
          endMs: normalizedNextSnapshot.endMs,
          sourceInMs: initial.sourceInMs + sourceDeltaMs,
        }, {
          mediaDurationMs: dragState.mediaDurationMs,
          minSegmentMs: dragState.minSegmentMs,
        });
      } else {
        normalizedNextSnapshot = {
          ...normalizedNextSnapshot,
          startMs: clampedStartMs,
        };
      }
    }

    let nextLaneId = dragState.latestLaneId;
    let nextLaneOrder = dragState.latestLaneOrder;
    let laneHasConflict = false;
    if (dragState.mode === 'move-track') {
      const lanePreview = resolveTrackLanePreviewFromClientY(event.clientY, {
        sourceLaneId: dragState.initialLaneId,
        allowInsertAtEnd: dragState.allowNewLaneDrop,
      });
      if (lanePreview.mode === 'compact' && lanePreview.targetLaneId) {
        nextLaneId = lanePreview.targetLaneId;
        nextLaneOrder = lanePreview.laneOrder;
        laneHasConflict = liveResolvedTracks.some((track) => {
          if (track.layerId === dragState.layerId) return false;
          if (track.laneId !== lanePreview.targetLaneId) return false;
          return normalizedNextSnapshot.startMs < track.endMs - 1 && normalizedNextSnapshot.endMs > track.startMs + 1;
        });
      } else {
        nextLaneId = dragState.insertLaneId;
        nextLaneOrder = Number.isFinite(liveMaxLaneOrder) ? liveMaxLaneOrder + 1 : dragState.initialLaneOrder + 1;
      }

      const preview: TrackLaneDragPreview = {
        ...lanePreview,
        startMs: normalizedNextSnapshot.startMs,
        durationMs: Math.max(dragState.minSegmentMs, normalizedNextSnapshot.endMs - normalizedNextSnapshot.startMs),
        hasConflict: laneHasConflict,
      };
      setTrackLaneDragPreview((current) => {
        if (
          current &&
          current.mode === preview.mode &&
          current.targetLaneId === preview.targetLaneId &&
          current.previewLayerId === preview.previewLayerId &&
          current.insertBeforeLayerId === preview.insertBeforeLayerId &&
          current.insertAtEnd === preview.insertAtEnd &&
          current.hasConflict === preview.hasConflict &&
          Math.abs(current.startMs - preview.startMs) < 1 &&
          Math.abs(current.durationMs - preview.durationMs) < 1
        ) {
          return current;
        }
        return preview;
      });
    } else {
      setTrackLaneDragPreview(null);
    }

    if (dragState.mode === 'move-track' && laneHasConflict) {
      return;
    }

    const normalizedSnapshotsByLayerId = {
      ...nextSnapshotsByLayerId,
      [dragState.layerId]: normalizedNextSnapshot,
    };
    const previousSnapshots = dragState.latestSnapshotsByLayerId;
    const snapshotsChangedFromPrevious = !areTrackSnapshotMapsEqual(
      normalizedSnapshotsByLayerId,
      previousSnapshots
    );
    const laneChangedFromPrevious =
      nextLaneId !== dragState.latestLaneId ||
      Math.abs(nextLaneOrder - dragState.latestLaneOrder) > 0.0001;
    if (!snapshotsChangedFromPrevious && !laneChangedFromPrevious) {
      return;
    }

    dragState.latest = normalizedNextSnapshot;
    dragState.latestSnapshotsByLayerId = normalizedSnapshotsByLayerId;
    dragState.latestLaneId = nextLaneId;
    dragState.latestLaneOrder = nextLaneOrder;
    dragState.hasLaneMutated =
      nextLaneId !== dragState.initialLaneId ||
      Math.abs(nextLaneOrder - dragState.initialLaneOrder) > 0.0001;
    dragState.hasMutated = !areTrackSnapshotMapsEqual(
      dragState.initialSnapshotsByLayerId,
      normalizedSnapshotsByLayerId
    ) || dragState.hasLaneMutated || dragState.hasParamMutated;
    if (snapshotsChangedFromPrevious) {
      publishTrackDragTimingPreview({
        layerId: dragState.layerId,
        snapshotsByLayerId: normalizedSnapshotsByLayerId,
        fadeInRatio: null,
        fadeOutRatio: null,
      });
    }
  }, [
    kernel,
    maxLaneOrderRef,
    publishTrackDragTimingPreview,
    resolveTrackLanePreviewFromClientY,
    resolvedTracksRef,
    setDragSnapGuideMs,
    setTrackLaneDragPreview,
  ]);

  useEffect(() => {
    handleTrackDragMoveRef.current = handleTrackDragMove;
  }, [handleTrackDragMove]);

  const startTrackDrag = useCallback((
    track: ResolvedWorkspaceTimelineTrack,
    mode: TrackDragMode,
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (event.button !== 0 || !kernel.current) return;
    const liveResolvedTracks = resolvedTracksRef.current;
    const liveLaneRows = laneRowsRef.current;
    const liveRulerRangeMs = rulerRangeMsRef.current;
    selectTrackFromPointer(track, event);
    event.preventDefault();
    event.stopPropagation();

    const eventTarget = event.currentTarget as HTMLElement;
    const laneElement = eventTarget.closest('[data-track-lane="true"]') as HTMLElement | null;
    const laneRect = laneElement?.getBoundingClientRect() ?? null;
    if (!laneRect || laneRect.width <= 0) return;

    const initialSnapshot = normalizeTrackSnapshot(
      {
        startMs: track.startMs,
        endMs: track.endMs,
        sourceInMs: track.sourceInMs,
      },
      {
        mediaDurationMs: track.mediaDurationMs,
        minSegmentMs: track.minSegmentMs,
      }
    );
    const initialSnapshotsByLayerId: TimelineTrackSnapshotMap = liveResolvedTracks.reduce((acc, resolvedTrack) => {
      acc[resolvedTrack.layerId] = normalizeTrackSnapshot(
        {
          startMs: resolvedTrack.startMs,
          endMs: resolvedTrack.endMs,
          sourceInMs: resolvedTrack.sourceInMs,
        },
        {
          mediaDurationMs: resolvedTrack.mediaDurationMs,
          minSegmentMs: resolvedTrack.minSegmentMs,
        }
      );
      return acc;
    }, {} as TimelineTrackSnapshotMap);
    if (!initialSnapshotsByLayerId[track.layerId]) {
      initialSnapshotsByLayerId[track.layerId] = initialSnapshot;
    }

    pausePlaybackForEdit();
    endTrackOrderDragRef.current(true);
    clearTrackOrderDragListeners();
    endTrackDragRef.current(true);
    clearTrackDragListeners();

    const sourceLaneTrackCount = Math.max(
      1,
      liveLaneRows.find((laneRow) => laneRow.laneId === track.laneId)?.tracks.length ?? 1
    );
    const initialLaneId = track.laneId;
    setShowEndInsertLaneWhileDragging(mode === 'move-track' && sourceLaneTrackCount > 1);
    setActiveTrackDragInitialLaneId(initialLaneId);
    trackDragStateRef.current = {
      layerId: track.layerId,
      trackName: track.name,
      allowNewLaneDrop: sourceLaneTrackCount > 1,
      insertLaneId: sourceLaneTrackCount > 1
        ? `lane-${track.layerId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        : track.layerId,
      initialLaneId,
      initialLaneOrder: track.laneOrder,
      latestLaneId: track.laneId,
      latestLaneOrder: track.laneOrder,
      hasLaneMutated: false,
      hasParamMutated: false,
      initialEffectRatio:
        mode === 'fade-in-end'
          ? track.fadeInRatio
          : (mode === 'fade-out-start' ? track.fadeOutRatio : 0),
      latestEffectRatio:
        mode === 'fade-in-end'
          ? track.fadeInRatio
          : (mode === 'fade-out-start' ? track.fadeOutRatio : 0),
      allowSourceTrim: track.layerType !== 'image',
      mode,
      pointerStartX: event.clientX,
      laneWidthPx: laneRect.width,
      durationMsAtStart: Math.max(1, liveRulerRangeMs),
      mediaDurationMs: track.mediaDurationMs,
      minSegmentMs: track.minSegmentMs,
      initial: initialSnapshot,
      latest: initialSnapshot,
      initialSnapshotsByLayerId,
      latestSnapshotsByLayerId: Object.fromEntries(
        Object.entries(initialSnapshotsByLayerId).map(([layerId, snapshot]) => [layerId, { ...snapshot }])
      ),
      edgeSnapAnchorsMs: Array.from(
        new Set(
          liveResolvedTracks
            .filter((resolvedTrack) => resolvedTrack.layerId !== track.layerId)
            .flatMap((resolvedTrack) => [
              Math.max(0, Math.round(resolvedTrack.startMs)),
              Math.max(0, Math.round(resolvedTrack.endMs)),
            ])
            .filter((value) => Number.isFinite(value))
        )
      ),
      hasMutated: false,
    };
    setDragSnapGuideMs(null);
    publishTrackDragTimingPreview(null);
    setTrackLaneDragPreview(null);
    setActiveDragTrackId(track.layerId);

    const handleMove = (moveEvent: MouseEvent) => {
      handleTrackDragMoveRef.current(moveEvent);
    };
    const handleUp = (upEvent: MouseEvent) => {
      handleTrackDragMoveRef.current(upEvent);
      endTrackDragRef.current(true);
    };

    trackDragListenersRef.current = { move: handleMove, up: handleUp };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [
    clearTrackDragListeners,
    clearTrackOrderDragListeners,
    kernel,
    laneRowsRef,
    pausePlaybackForEdit,
    publishTrackDragTimingPreview,
    resolvedTracksRef,
    rulerRangeMsRef,
    selectTrackFromPointer,
    setActiveDragTrackId,
    setActiveTrackDragInitialLaneId,
    setDragSnapGuideMs,
    setShowEndInsertLaneWhileDragging,
    setTrackLaneDragPreview,
  ]);

  const handleTrackOrderDragMove = useCallback((event: MouseEvent) => {
    const dragState = trackOrderDragStateRef.current;
    if (!dragState) return;
    event.preventDefault();
    const offsetRows = Math.round((event.clientY - dragState.pointerStartY) / Math.max(1, dragState.rowStepPx));
    const nextIndex = clamp(
      dragState.initialIndex + offsetRows,
      0,
      Math.max(0, dragState.initialTrackLayerIds.length - 1)
    );
    if (nextIndex === dragState.latestIndex) return;
    const nextOrder = moveArrayItem(dragState.initialTrackLayerIds, dragState.initialIndex, nextIndex);
    if (areStringArraysEqual(nextOrder, dragState.latestTrackLayerIds)) return;
    dragState.latestIndex = nextIndex;
    dragState.latestTrackLayerIds = nextOrder;
    dragState.hasMutated = !areStringArraysEqual(dragState.initialTrackLayerIds, nextOrder);
    applyTrackLayerOrderToWorkspace(nextOrder);
  }, [applyTrackLayerOrderToWorkspace]);

  useEffect(() => {
    handleTrackOrderDragMoveRef.current = handleTrackOrderDragMove;
  }, [handleTrackOrderDragMove]);

  const startTrackOrderDrag = useCallback((
    track: ResolvedWorkspaceTimelineTrack,
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (event.button !== 0 || !kernel.current) return;
    event.preventDefault();
    event.stopPropagation();
    selectTrackFromPointer(track, event);
    const isSplitToolActive = activeTool === 'scissors';
    const isSoundToolActive = activeTool === 'sound';
    if (isSplitToolActive || isSoundToolActive) return;

    const rowElement = event.currentTarget.closest('[data-track-row="true"]') as HTMLElement | null;
    const rowRect = rowElement?.getBoundingClientRect() ?? null;
    if (!rowRect || rowRect.height <= 0) return;

    const orderedTrackLayerIds = displayTrackOrderLayerIdsRef.current;
    const initialIndex = orderedTrackLayerIds.indexOf(track.layerId);
    if (initialIndex < 0 || orderedTrackLayerIds.length < 2) return;

    pausePlaybackForEdit();
    endTrackDragRef.current(true);
    clearTrackDragListeners();
    endTrackOrderDragRef.current(true);
    clearTrackOrderDragListeners();

    trackOrderDragStateRef.current = {
      trackName: track.name,
      pointerStartY: event.clientY,
      rowStepPx: Math.max(1, rowRect.height + 4),
      initialIndex,
      latestIndex: initialIndex,
      initialTrackLayerIds: [...orderedTrackLayerIds],
      latestTrackLayerIds: [...orderedTrackLayerIds],
      hasMutated: false,
    };
    setActiveTrackOrderDragId(track.layerId);

    const handleMove = (moveEvent: MouseEvent) => {
      handleTrackOrderDragMoveRef.current(moveEvent);
    };
    const handleUp = () => {
      endTrackOrderDragRef.current(true);
    };

    trackOrderDragListenersRef.current = { move: handleMove, up: handleUp };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [
    activeTool,
    clearTrackDragListeners,
    clearTrackOrderDragListeners,
    displayTrackOrderLayerIdsRef,
    kernel,
    pausePlaybackForEdit,
    selectTrackFromPointer,
    setActiveTrackOrderDragId,
  ]);

  useEffect(() => {
    endTrackDragRef.current = endTrackDrag;
  }, [endTrackDrag]);

  useEffect(() => {
    endTrackOrderDragRef.current = endTrackOrderDrag;
  }, [endTrackOrderDrag]);

  useEffect(() => {
    return () => {
      endTrackDragRef.current(false);
      endTrackOrderDragRef.current(false);
    };
  }, []);

  const registerTrackLaneRef = useCallback((
    laneTracks: ResolvedWorkspaceTimelineTrack[],
    element: HTMLDivElement | null
  ) => {
    laneTracks.forEach((laneTrack) => {
      timelineTrackLaneRefs.current[laneTrack.layerId] = element;
    });
  }, []);

  return useMemo<UseWorkspaceTimelineInteractionControllerResult>(() => ({
    timelineTrackLaneRefs,
    timelineInsertEndLaneRef,
    handleTimelineDragOver,
    handleTimelineDragLeave,
    handleTimelineDrop,
    registerTrackLaneRef,
    startTrackDrag,
    startTrackOrderDrag,
    resolveSplitMsFromLanePointer,
    resolveTrackFromLanePointer,
  }), [
    handleTimelineDragLeave,
    handleTimelineDragOver,
    handleTimelineDrop,
    registerTrackLaneRef,
    resolveSplitMsFromLanePointer,
    resolveTrackFromLanePointer,
    startTrackDrag,
    startTrackOrderDrag,
  ]);
};
